import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RedditAnalytics from '../components/RedditAnalytics';
import { API_BASE_URL } from '../utils/constants';

interface RedditPost {
  postId: string;
  subreddit: string;
  title: string;
  url: string;
  relevanceScore: number;
  engagement: number;
  hoursAgo: number;
  suggestedAction: string;
  dosewiseRelevance: string;
  score?: number;
  numComments?: number;
  author?: string;
  selftext?: string;
}

interface RedditStatus {
  status: string;
  authenticated: boolean;
  message: string;
  authUrl?: string;
}

const AdminRedditMarketingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [redditStatus, setRedditStatus] = useState<RedditStatus | null>(null);
  const [suggestions, setSuggestions] = useState<RedditPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState('mounjaro,glp1,weightloss,diabetes');
  const [limit, setLimit] = useState(10);
  const [filter, setFilter] = useState('all'); // all, high-priority, recent, high-engagement

  // Check if user is admin
  useEffect(() => {
    if (!user || user.userType !== 'admin') {
      navigate('/login');
    }
  }, [user, navigate]);

  // Check Reddit authentication status
  useEffect(() => {
    checkRedditStatus();
  }, []);

  const checkRedditStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/reddit/test`);
      const data = await response.json();
      setRedditStatus(data);
      
      if (data.status === 'authenticated' && data.suggestions) {
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error checking Reddit status:', error);
    }
  };

  const authenticateReddit = async () => {
    if (!redditStatus?.authUrl) return;
    window.open(redditStatus.authUrl, '_blank');
    
    // Poll for authentication completion
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/reddit/test`);
        const data = await response.json();
        
        if (data.status === 'authenticated') {
          setRedditStatus(data);
          setSuggestions(data.suggestions || []);
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Error polling Reddit status:', error);
      }
    }, 2000);
  };

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/reddit/promotion-suggestions?keywords=${selectedKeywords}&limit=${limit}`);
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (action: string) => {
    if (action.includes('High priority')) return 'text-red-600 bg-red-50 border-red-200';
    if (action.includes('Medium priority')) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getEngagementColor = (engagement: number) => {
    if (engagement > 1000) return 'text-green-600';
    if (engagement > 500) return 'text-blue-600';
    if (engagement > 100) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const getTimeColor = (hoursAgo: number) => {
    if (hoursAgo < 24) return 'text-green-600';
    if (hoursAgo < 168) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const filteredSuggestions = suggestions.filter(post => {
    switch (filter) {
      case 'high-priority':
        return post.suggestedAction.includes('High priority');
      case 'recent':
        return post.hoursAgo < 24;
      case 'high-engagement':
        return post.engagement > 500;
      default:
        return true;
    }
  });

  const openPost = (url: string) => {
    window.open(url, '_blank');
  };

  if (!user || user.userType !== 'admin') {
    return <div className="p-8">Access denied. Admin privileges required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Reddit Marketing Dashboard</h1>
              <p className="text-gray-600 mt-2">Find and engage with relevant posts to promote DoseWise</p>
            </div>
            <div className="flex items-center space-x-4">
              {redditStatus?.status === 'authenticated' ? (
                <div className="flex items-center text-green-600">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  Connected to Reddit
                </div>
              ) : (
                <button
                  onClick={authenticateReddit}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Connect to Reddit
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords
              </label>
              <input
                type="text"
                value={selectedKeywords}
                onChange={(e) => setSelectedKeywords(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="mounjaro,glp1,weightloss"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Limit
              </label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5 posts</option>
                <option value={10}>10 posts</option>
                <option value={15}>15 posts</option>
                <option value={20}>20 posts</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter
              </label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All posts</option>
                <option value="high-priority">High priority</option>
                <option value="recent">Recent (24h)</option>
                <option value="high-engagement">High engagement</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchSuggestions}
                disabled={loading || redditStatus?.status !== 'authenticated'}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Loading...' : 'Refresh Posts'}
              </button>
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="mb-6">
          <RedditAnalytics />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-2xl font-bold text-gray-900">{filteredSuggestions.length}</div>
            <div className="text-gray-600">Posts Found</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-2xl font-bold text-red-600">
              {filteredSuggestions.filter(p => p.suggestedAction.includes('High priority')).length}
            </div>
            <div className="text-gray-600">High Priority</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-2xl font-bold text-green-600">
              {filteredSuggestions.filter(p => p.hoursAgo < 24).length}
            </div>
            <div className="text-gray-600">Recent (24h)</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-2xl font-bold text-blue-600">
              {filteredSuggestions.filter(p => p.engagement > 500).length}
            </div>
            <div className="text-gray-600">High Engagement</div>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSuggestions.map((post, index) => (
            <div key={post.postId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        r/{post.subreddit}
                      </span>
                      <span className={getEngagementColor(post.engagement)}>
                        {post.engagement} engagement
                      </span>
                      <span className={getTimeColor(post.hoursAgo)}>
                        {post.hoursAgo < 24 
                          ? `${Math.round(post.hoursAgo)}h ago`
                          : `${Math.round(post.hoursAgo / 24)}d ago`
                        }
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-2xl font-bold text-gray-900">
                      {post.relevanceScore.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">Relevance</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(post.suggestedAction)}`}>
                  {post.suggestedAction}
                </div>
                
                <div className="mt-4">
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>DoseWise Relevance:</strong>
                  </div>
                  <p className="text-sm text-gray-700">{post.dosewiseRelevance}</p>
                </div>

                {/* Actions */}
                <div className="mt-6 flex space-x-3">
                  <button
                    onClick={() => openPost(post.url)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                  >
                    View Post
                  </button>
                  <button
                    onClick={() => openPost(`${post.url}?context=3`)}
                    className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors text-sm"
                  >
                    View Comments
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredSuggestions.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
            <p className="text-gray-600">
              Try adjusting your keywords or filters to find relevant posts.
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Finding relevant posts...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRedditMarketingPage; 